import type { ComponentPropsWithoutRef, ReactNode } from 'react';

import { cn } from '@/lib/cn';

/** Champ sur fond encre (maquette, formulaire de contact). */
const CONTROL =
  'w-full min-h-[46px] rounded-sm border border-blanc/20 bg-blanc/6 px-[15px] py-[13px] ' +
  'font-sans text-body-s leading-[1.4] text-blanc placeholder:text-blanc/55 ' +
  'transition-[border-color,box-shadow] ' +
  'focus:border-ciel focus:shadow-[0_0_0_3px_color-mix(in_srgb,var(--color-ciel)_25%,transparent)] focus:outline-none ' +
  'aria-invalid:border-ciel';

type BaseProps = {
  id: string;
  label: string;
  /** Message d'erreur explicite : la gravité passe par les mots, pas par une couleur d'alerte. */
  error?: string;
  className?: string;
};

type InputProps = BaseProps & Omit<ComponentPropsWithoutRef<'input'>, 'id' | 'className'>;
type TextareaProps = BaseProps & Omit<ComponentPropsWithoutRef<'textarea'>, 'id' | 'className'>;
type SelectProps = BaseProps &
  Omit<ComponentPropsWithoutRef<'select'>, 'id' | 'className'> & { children: ReactNode };

function Wrapper({ id, label, error, className, children }: BaseProps & { children: ReactNode }) {
  return (
    <div className={cn('grid gap-2', className)}>
      <label htmlFor={id} className="label text-blanc/75">
        {label}
      </label>
      {children}
      {/* Ciel sur encre : 9.6:1. */}
      {error && (
        <p id={`${id}-error`} className="text-body-xs font-medium text-ciel">
          {error}
        </p>
      )}
    </div>
  );
}

const describedBy = (id: string, error?: string) => (error ? `${id}-error` : undefined);

export function TextField({ id, label, error, className, ...rest }: InputProps) {
  return (
    <Wrapper id={id} label={label} error={error} className={className}>
      <input id={id} aria-invalid={error ? true : undefined} aria-describedby={describedBy(id, error)} className={CONTROL} {...rest} />
    </Wrapper>
  );
}

export function TextAreaField({ id, label, error, className, ...rest }: TextareaProps) {
  return (
    <Wrapper id={id} label={label} error={error} className={className}>
      <textarea
        id={id}
        aria-invalid={error ? true : undefined}
        aria-describedby={describedBy(id, error)}
        className={cn(CONTROL, 'min-h-[130px] resize-y')}
        {...rest}
      />
    </Wrapper>
  );
}

export function SelectField({ id, label, error, className, children, ...rest }: SelectProps) {
  return (
    <Wrapper id={id} label={label} error={error} className={className}>
      <select id={id} className={cn(CONTROL, '[&_option]:text-encre')} {...rest}>
        {children}
      </select>
    </Wrapper>
  );
}
